import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import { Hash, LockKeyhole, Menu, Moon, Sun } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useChama } from "@/hooks/useChama";
import { useControlPlane } from "@/hooks/useControlPlane";
import { useChannelMessages } from "@/hooks/useChannelMessages";
import { useChatActions } from "@/hooks/useChatActions";
import { useMembers } from "@/hooks/useMembers";
import { useTheme } from "@/hooks/useTheme";
import { ChannelSidebar } from "@/components/chama/chat/ChannelSidebar";
import { MessageList } from "@/components/chama/chat/MessageList";
import { Composer } from "@/components/chama/chat/Composer";
import { MemberList } from "@/components/chama/chat/MemberList";
import { APP_NAME } from "@/lib/concord/config";
import type { ChatMessage } from "@/lib/concord/types";

function ChatSkeleton() {
  return (
    <div className="flex h-dvh">
      <div className="hidden w-64 border-r p-4 md:block space-y-3">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="flex-1 p-6 space-y-6">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="size-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChatPage() {
  useSeoMeta({ title: `${APP_NAME} — Chat` });
  const { user } = useCurrentUser();
  const chama = useChama();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const membership = chama.membership;
  const { control, isLoading: controlLoading } = useControlPlane(membership);

  const channels = useMemo(
    () => control.channels.filter((c) => !c.deleted && !c.private),
    [control.channels],
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const channel = channels.find((c) => c.id === activeId) ?? channels[0];

  const {
    messages,
    isLoading: messagesLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    appendWrap,
  } = useChannelMessages(membership, channel, {
    myPub: user?.pubkey,
    banlist: control.banlist,
  });

  const { send, react, remove } = useChatActions(
    membership,
    channel?.id,
    appendWrap,
  );
  const { members } = useMembers(membership, control);
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user) navigate("/");
    else if (chama.status !== "loading" && chama.status !== "member") navigate("/");
  }, [user, chama.status, navigate]);

  if (!user || chama.status !== "member") {
    return <ChatSkeleton />;
  }

  const communityName =
    control.metadata?.name || membership?.current.name || "Chama";

  const sidebar = (
    <ChannelSidebar
      communityName={communityName}
      channels={channels}
      activeChannelId={channel?.id}
      memberCount={members.length}
      isModerator={chama.isModerator}
      onSelect={(id) => {
        setActiveId(id);
        setSidebarOpen(false);
      }}
    />
  );

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-card px-3">
        <div className="flex min-w-0 items-center gap-2">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open channels">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              {sidebar}
            </SheetContent>
          </Sheet>
          {channel ? (
            <div className="flex min-w-0 items-center gap-2">
              <Hash className="size-5 shrink-0 text-muted-foreground" />
              <h1 className="truncate font-display text-lg font-semibold">
                {channel.name}
              </h1>
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                <LockKeyhole className="size-3" />
                E2EE
              </span>
            </div>
          ) : (
            <Skeleton className="h-6 w-32" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </Button>
          <LoginArea className="max-w-40" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-64 shrink-0 border-r md:block">{sidebar}</aside>

        <main className="flex min-w-0 flex-1 flex-col bg-background">
          {controlLoading ? (
            <div className="flex-1 p-6 space-y-6">
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="size-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : channel ? (
            <>
              <MessageList
                messages={messages}
                isLoading={messagesLoading}
                hasNextPage={Boolean(hasNextPage)}
                isFetchingNextPage={isFetchingNextPage}
                onLoadOlder={() => void fetchNextPage()}
                myPub={user.pubkey}
                channelName={channel.name}
                onReact={(m, emoji) =>
                  react.mutate({ targetId: m.id, targetPub: m.pubkey, emoji })
                }
                onDelete={(m) => setPendingDelete(m)}
              />
              <Composer
                channelName={channel.name}
                sending={send.isPending}
                onSend={(content) => send.mutate(content)}
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="rounded-xl border border-dashed p-12 text-center">
                <p className="text-muted-foreground max-w-sm">
                  No channels yet.{" "}
                  {chama.isModerator
                    ? "Open Moderation to create the first one."
                    : "The moderator hasn't opened any channels yet — check back soon."}
                </p>
              </div>
            </div>
          )}
        </main>

        <MemberList members={members} />
      </div>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              Members will stop seeing it. The sealed copy on relays can only
              be removed by relays honoring giftwrap deletions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove.mutate(pendingDelete.id);
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
